import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Autocomplete from '../../../../../component/common/Autocomplete';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import CommentsPanel from '../components/CommentsPanel';
import { StandardDropdown, FIELD_CLASS } from '../../../../../component/common/StandardUI';
import { RecycleBinIcon } from '../components/icons';
import ChecklistCheckbox from '../components/ChecklistCheckbox';
import { getOwnerRoleLabel } from '../components/ownerRole';
import checklistIcon from '../../../../../assets/svg/icons/List-To-Do-Tasks-Checklist--Streamline-Flex.svg';
import alarmClockIcon from '../../../../../assets/svg/icons/Alarm-Clock--Streamline-Flex.svg';
import pencilIcon from '../../../../../assets/svg/icons/Pencil--Streamline-Flex.svg';

const ViewMilestone = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();

  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [addingOwner, setAddingOwner] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const fetchMilestone = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getMilestoneById(id);
      if (response?.data?.meta?.code === 1) {
        setMilestone(response?.data?.data);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to load milestone');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load milestone');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMilestone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleChecklist = async (checklistItemId) => {
    try {
      setTogglingId(checklistItemId);
      const response = await CompanyApi.toggleMilestoneChecklistItem(id, checklistItemId);
      if (response?.data?.meta?.code === 1) {
        fetchMilestone();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to update checklist item');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to update checklist item');
    } finally {
      setTogglingId(null);
    }
  };

  const searchOwners = async (searchKey) => {
    try {
      setOwnerLoading(true);
      const response = await CompanyApi.getPeoplePlanOwners(searchKey);
      if (response?.data?.meta?.code === 1) {
        setOwnerOptions(response?.data?.data || []);
      }
    } catch (error) {
      errorToast(error.message || 'Failed to search admins');
    } finally {
      setOwnerLoading(false);
    }
  };

  const handleAddOwner = async (user) => {
    const existingIds = (milestone.assignedOwner || []).map((o) => o._id || o);
    if (existingIds.includes(user._id)) {
      setAddingOwner(false);
      return;
    }
    try {
      setLoading(true);
      const response = await CompanyApi.updateMilestone(id, {
        title: milestone.title,
        endDate: milestone.endDate,
        assignedOwner: [...existingIds, user._id],
        checklist: (milestone.checklist || []).map((item) => ({
          text: item.text,
          isCompleted: !!item.isCompleted
        }))
      });
      if (response?.data?.meta?.code === 1) {
        successToast('Owner added');
        setAddingOwner(false);
        fetchMilestone();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to add owner');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to add owner');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (text) => {
    try {
      setCommentSubmitting(true);
      const response = await CompanyApi.addMilestoneComment(id, { text });
      if (response?.data?.meta?.code === 1) {
        fetchMilestone();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to add comment');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this milestone? This cannot be undone.')) return;
    try {
      setLoading(true);
      const response = await CompanyApi.deleteMilestone(id);
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Milestone deleted');
        navigate(`/${slug}/people-plan/milestones`);
      } else {
        setLoading(false);
        errorToast(response?.data?.meta?.message || 'Failed to delete milestone');
      }
    } catch (error) {
      setLoading(false);
      errorToast(error.message || 'Failed to delete milestone');
    }
  };

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/${slug}/people-plan/milestones/edit/${id}`);
    } else if (action === 'delete') {
      handleDelete();
    }
  };

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Milestones', href: `/${slug}/people-plan/milestones`, current: false },
    { name: milestone?.title || 'Milestone', href: '#', current: true }
  ];

  if (loading && !milestone) {
    return (
      <div className="relative lg:mb-[45px]">
        <Loader />
      </div>
    );
  }

  if (!milestone) return null;

  // Admins can manage any record; a sub admin only the ones they created.
  const canManage =
    !isSubAdmin || (milestone.createdBy?._id || milestone.createdBy) === userData?.id;

  const checklist = milestone.checklist || [];
  const completedCount = checklist.filter((item) => item.isCompleted).length;
  const progress = checklist.length > 0 ? Math.round((completedCount / checklist.length) * 100) : 0;

  return (
    <div className="relative lg:mb-[45px]">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>{milestone.title} - People plan | Shoorah Admin</title>
      </Helmet>
      <Topbar title={milestone.title} infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="flex items-center justify-end gap-3 mb-6">
          {canManage && (
            <StandardDropdown
              label="Actions"
              align="right"
              options={[
                {
                  label: 'Edit milestone',
                  value: 'edit',
                  icon: <img src={pencilIcon} alt="" className="w-5 h-5 dark:invert" />
                },
                {
                  label: 'Delete milestone',
                  value: 'delete',
                  icon: <RecycleBinIcon className="w-5 h-5" />,
                  className: 'text-red-500 hover:text-red-600'
                }
              ]}
              onChange={handleAction}
            />
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <div className="flex items-start gap-3">
              <img src={checklistIcon} alt="" className="w-6 h-6 shrink-0 mt-0.5 dark:invert" />
              <div>
                <p className="text-lg font-medium text-gray-700 dark:text-gray-200">
                  {milestone.title}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  View and manage all aspects of your milestone in one place.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
                Progress
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                Progress updates in real-time as you complete the checklist.
              </p>
              <p className="text-xs text-gray-400 text-right mb-1">
                {progress}% until checklist complete
              </p>
              <div className="h-1.5 w-full rounded-full bg-shoorah-progressGreenLight dark:bg-shoorah-progressGreen/30 overflow-hidden">
                <div
                  className="h-full rounded-full bg-shoorah-progressGreen"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">Deadline</h3>
                {canManage && (
                  <button
                    type="button"
                    aria-label="Edit deadline"
                    onClick={() => navigate(`/${slug}/people-plan/milestones/edit/${id}`)}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <img src={pencilIcon} alt="" className="w-4 h-4 dark:invert" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">Track your deadline to stay aligned.</p>
              <div className={`${FIELD_CLASS} flex items-center gap-2`}>
                <img src={alarmClockIcon} alt="" className="w-5 h-5 shrink-0 dark:invert" />
                {milestone.endDate ? moment(milestone.endDate).format('Do MMMM YYYY') : '—'}
              </div>
            </div>
          </div>

          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
              Milestone owners
            </h3>
            <p className="text-sm text-gray-400 mb-4">People responsible for this milestone.</p>
            <div className="space-y-3">
              {(milestone.assignedOwner || []).length === 0 && (
                <p className="text-sm text-gray-400">No owners assigned.</p>
              )}
              {(milestone.assignedOwner || []).map((owner, index) => (
                <div key={owner._id || index} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-shoorah-bodyBackground dark:bg-shoorah-darkBgColor flex items-center justify-center text-xs font-medium text-gray-500 shrink-0">
                    {(owner.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {owner.name || owner}
                    </p>
                    {getOwnerRoleLabel(owner) && (
                      <p className="text-xs text-gray-400">{getOwnerRoleLabel(owner)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {canManage &&
              (addingOwner ? (
                <div className="mt-3">
                  <Autocomplete
                    placeholder="Search admins..."
                    options={ownerOptions}
                    onSearch={searchOwners}
                    onSelect={handleAddOwner}
                    loading={ownerLoading}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingOwner(true)}
                  className="text-sm text-shoorah-primary font-medium mt-3"
                >
                  Add owner
                </button>
              ))}
          </div>

          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
              Checklist
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Track your milestone progress and mark your checklist as complete.
            </p>
            {checklist.length === 0 && <p className="text-sm text-gray-400">No checklist items.</p>}
            <div className="space-y-2">
              {checklist.map((item) => (
                <div key={item._id} className="flex items-center gap-2">
                  <ChecklistCheckbox
                    checked={!!item.isCompleted}
                    disabled={togglingId === item._id}
                    onToggle={() => handleToggleChecklist(item._id)}
                    label={`Mark "${item.text}" complete`}
                  />
                  <div className={`${FIELD_CLASS} flex-1 flex items-center`}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <CommentsPanel
              comments={milestone.comments || []}
              onAddComment={handleAddComment}
              submitting={commentSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewMilestone;
